import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Localization } from 'connex-cds';
import * as S from './styles';

export const CreateTicketModal = ({ open, assignment, isSubmitting, onClose, onSubmit }) => {
  const translateMessage = Localization.useTranslateMessage();
  const [netQuantity, setNetQuantity] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');

  const defaultQuantity = assignment?.primaryProduct?.quantity;

  useEffect(() => {
    if (!open) {
      return;
    }
    setNetQuantity(defaultQuantity != null && defaultQuantity !== '' ? String(defaultQuantity) : '');
    setTicketNumber('');
  }, [open, assignment?.scheduledLoadRef, defaultQuantity]);

  const quantityValue = Number.parseFloat(netQuantity);
  const isQuantityValid = Number.isFinite(quantityValue) && quantityValue > 0;
  const canSubmit = isQuantityValid && !isSubmitting;

  const contextRows = useMemo(
    () => [
      { label: translateMessage('gridHeader_orderNumber'), value: assignment?.orderId },
      { label: translateMessage('gridHeader_loadNumber'), value: assignment?.loadNumber },
      { label: translateMessage('gridHeader_product'), value: assignment?.primaryProduct?.name },
      { label: translateMessage('gridHeader_customer'), value: assignment?.customerName },
    ],
    [assignment, translateMessage]
  );

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    const trimmedTicketNumber = ticketNumber?.trim?.();
    onSubmit?.({
      quantity: quantityValue,
      ...(trimmedTicketNumber ? { ticketNumber: trimmedTicketNumber } : {}),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="modal-create-ticket"
    >
      <DialogTitle>{translateMessage('loadAssignment_ticketInformation')}</DialogTitle>
      <DialogContent>
        {contextRows?.map?.(row => (
          <S.DetailRow key={row?.label}>
            <S.DetailLabel>{row?.label}</S.DetailLabel>
            <S.DetailValue>{row?.value || '—'}</S.DetailValue>
          </S.DetailRow>
        ))}
        <S.CreateTicketField sx={{ mt: 2 }}>
          <TextField
            required
            fullWidth
            type="number"
            label={translateMessage('loadAssignment_netQuantity')}
            placeholder={translateMessage('loadAssignment_netQuantityPlaceholder')}
            value={netQuantity}
            onChange={event => setNetQuantity(event?.target?.value)}
            inputProps={{ min: 0, step: 'any' }}
            data-testid="input-net-quantity"
          />
        </S.CreateTicketField>
        <S.CreateTicketField>
          <TextField
            fullWidth
            label={translateMessage('loadAssignment_referenceTicketNumber')}
            placeholder={translateMessage('loadAssignment_referenceTicketPlaceholder')}
            value={ticketNumber}
            onChange={event => setTicketNumber(event?.target?.value)}
            data-testid="input-reference-ticket"
          />
        </S.CreateTicketField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary" disabled={isSubmitting} data-testid="button-cancel-create-ticket">
          {translateMessage('loadAssignment_cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          data-testid="button-create-ticket"
        >
          {translateMessage('loadAssignment_createTicket')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
